import { z } from "zod";

export const GET_EDIT_CAPABILITIES = "connection-edit-capabilities";

export const EditableFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  // The backend may add kinds the UI does not know yet.
  kind: z.string(),
  required: z.boolean(),
  max_length: z.number().nullable(),
  options: z.array(z.string()).optional(),
  value: z.string().nullable().optional(),
  show_when: z.record(z.array(z.string())).optional(),
  group: z.string().optional(),
  required_in_group: z.boolean().optional(),
  encoding: z.string().optional(),
  help: z.string().optional(),
});

export const ReadOnlyFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  reason: z.string(),
  value: z.string().nullable().optional(),
});

export const EditCapabilitySchema = z.object({
  connection_type: z.string(),
  method: z.enum(["PATCH", "PUT"]),
  path: z.string(),
  body: z.enum(["partial", "replace"]),
  permission: z.string(),
  can_edit: z.boolean(),
  editable_fields: z.array(EditableFieldSchema),
  read_only_fields: z.array(ReadOnlyFieldSchema),
  connection_test: z.string().optional(),
});

export const GET_CONNECTION_EDIT_FORM = "connection-edit-form";
export const SAVE_CONNECTION_EDIT = "connection-edit-save";

export const ConnectionEditFormSchema = EditCapabilitySchema.extend({
  id: z.union([z.number(), z.string()]),
  display_name: z.string(),
  auth_type: z.string().nullable().optional(),
  base_body: z.record(z.unknown()).optional(),
});

export const EditCapabilitiesResponseSchema = z.object({
  enabled: z.boolean(),
  platform: z.string(),
  types: z.record(EditCapabilitySchema),
});

export type EditableField = z.infer<typeof EditableFieldSchema>;
export type ReadOnlyField = z.infer<typeof ReadOnlyFieldSchema>;
export type EditCapability = z.infer<typeof EditCapabilitySchema>;
export type ConnectionEditForm = z.infer<typeof ConnectionEditFormSchema>;
export type EditCapabilitiesResponse = z.infer<typeof EditCapabilitiesResponseSchema>;

/** The single place that decides whether a connection type is editable, from the capabilities response. */
export const getEditCapability = (
  capabilities: EditCapabilitiesResponse | undefined,
  connectionType: string
): EditCapability | null => {
  if (capabilities === undefined || !capabilities.enabled) return null;
  if (!Object.prototype.hasOwnProperty.call(capabilities.types, connectionType)) return null;
  return capabilities.types[connectionType];
};
